import { Alert } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import { Fragment, useCallback, useEffect, useState } from 'react';
import ReactPaginate from 'react-paginate';
import { translate } from 'react-switch-lang';
import { Button, Card, CardBody, CardHeader, Col, Container, Modal, ModalBody, ModalHeader, Row } from 'reactstrap';
import { DeleteButtonIcon } from 'src/component/Buttons/DeleteButtonIcon';
import { InfoButtonIcon } from 'src/component/Buttons/InfoButtonIcon';
import SweetAlert from 'sweetalert2';
import { useHttpClient } from '../../../../../security/hooks/axiosProvider';
import Breadcrumb from '../../../breadcrumb';
import styles from '../../../styles/style-setting.module.scss';
import CreateNatureProduct from '../create-nature-product/create-nature-product';
import DetailNatureProduct from '../detail-nature-product/detail-nature-product';
import EditNatureProduct from '../edit-nature-product/edit-nature-product';
import NatureProductColumns from './table-header/nature-product-columns';
const NUMBER_OF_NATUREPRODUCT_PER_PAGE = 15;

const ListNatureProduct: React.FC<{ t: (key: string) => string }> = (props) => {
  const httpClient = useHttpClient(true);
  const [natureProduct, setNatureProduct] = useState({});
  const [natureProducts, setNatureProducts] = useState([]);
  const [pageNumber, setPageNumber] = useState(0);
  const natureProductPerPage = NUMBER_OF_NATUREPRODUCT_PER_PAGE;
  const pagesVisited = pageNumber * NUMBER_OF_NATUREPRODUCT_PER_PAGE;
  let data = [];
  const [modelDetail, setModelDetail] = useState(false);
  const modelDetailToggle = (natureProductValue) => {
    setModelDetail(!modelDetail);
    setNatureProduct(natureProductValue);
  };
  const keycloak = useKeycloak().keycloak;
  const [role, setRole] = useState('');
  const [search, setSearch] = useState('');
  const [filterByActivity, setFilterByActivity] = useState('');
  const [activities, setActivities] = useState([]);
  const [spinner, setSpinner] = useState({ state: false, id: '' });
  const pageCount = Math.ceil(
    (search !== ''
      ? natureProducts.filter((natureProduct) => natureProduct.name.includes(search))
      : filterByActivity !== ''
      ? natureProducts.filter((natureProduct) => natureProduct.activity.name.includes(filterByActivity))
      : natureProducts
    ).length / natureProductPerPage
  );

  const getNatureProducts = useCallback(async () => {
    httpClient.current?.get(`/api/nature_product/all`).then((response) => {
      const sortedProducts = response.data.sort((a, b) => a.name.localeCompare(b.name));
      setNatureProducts(sortedProducts);
    });
  }, [httpClient]);
  
  const deleteLocality = async (natureProductId) => {
    await httpClient.current?.delete(`/api/nature_product/${natureProductId}`);
    getNatureProducts();
  };

  const changePage = ({ selected }) => {
    setPageNumber(selected);
  };

  const handleCheckBox = async (event, natureProduct) => {
    setSpinner({ state: true, id: natureProduct.id });
    const natureProductValidation = {
      id: natureProduct.id,
      name: natureProduct.name,
      description: natureProduct.description,
      activity: natureProduct.activity,
      valid: event.target.checked,
    };
    const response = httpClient.current?.put(`/api/nature_product/update`, natureProductValidation);
    if ((await response).status === 200) {
      getNatureProducts();
      setSpinner({ state: false, id: natureProduct.id });
    }
  };

  useEffect(() => {
    const getActivities = () => {
      httpClient.current?.get(`/api/activity/all`).then((response) => {
        setActivities(response.data);
      });
    };
    getActivities();
    getNatureProducts();
    if (keycloak.authenticated) {
      keycloak.hasResourceRole('ROLE_AGRISED_ADMIN_SUPER')
        ? setRole('super admin')
        : keycloak.hasResourceRole('ROLE_AGRISED_ADMIN')
        ? setRole('admin')
        : setRole('user');
    }
  }, [getNatureProducts, httpClient, keycloak]);

  let restPagination = natureProducts
    .filter((natureProduct) => natureProduct.name.toUpperCase().includes(search.toUpperCase()))
    .filter((natureProduct) => natureProduct.activity.name.toUpperCase().includes(filterByActivity.toUpperCase()))
    .slice(pagesVisited, pagesVisited + natureProductPerPage);

  restPagination.map((natureProduct) => {
    let action = (
      <div>
        <InfoButtonIcon
          onClick={() => modelDetailToggle(natureProduct)}
          title={props.t('more_details')}
          className={`fa fa-eye ${styles['action']}`}
        />
        <span>
          <EditNatureProduct natureproduct={natureProduct} natureproducts={getNatureProducts} />
        </span>
        <span>
          <DeleteButtonIcon
            title={props.t('delete_np')}
            onClick={() =>
              SweetAlert.fire({
                title: props.t('you_delete'),
                text: props.t('not_recover'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Ok',
                cancelButtonText: props.t('cancel'),
                reverseButtons: true,
              }).then((result) => {
                if (result.value) {
                  deleteLocality(natureProduct.id);
                  SweetAlert.fire(props.t('Deleted!'), props.t('isDeleted'), 'success');
                } else {
                  SweetAlert.fire(props.t('isNotDeleted'));
                }
              })
            }
            className={`fa fa-trash ${styles['trash']}`}
          />
        </span>
      </div>
    );
    let status =
      spinner.state === true && spinner.id === natureProduct.id ? (
        <div className={`loader-box  ${styles['spinner']}`}>
          <div className="loader-2"></div>
        </div>
      ) : (
        <input
          type="checkbox"
          disabled={role === 'admin'}
          className={styles['valid']}
          name="status"
          checked={natureProduct?.valid}
          onChange={(e) => handleCheckBox(e, natureProduct)}
        />
      );
    return data.push({
      id: natureProduct.id,
      name: natureProduct.name,
      description: natureProduct.description,
      status: status,
      action: action,
    });
  });

  return (
    <>
      <div>
        <Fragment>
          <Breadcrumb parent={props.t('list_np')} url={'settings/nature-product'} />
          <Container fluid={true}>
            <Row>
              <Col sm="12">
                <Card>
                  <CardHeader>
                    <div className="float-right">
                      <CreateNatureProduct natureProducts={getNatureProducts} />
                    </div>
                  </CardHeader>
                  <CardBody>
                    <div className="row">
                      <div className="col">
                        <input
                          className="form-control mb-2"
                          onChange={(event) => {
                            setSearch(event.target.value);
                            changePage({ selected: 0 });
                          }}
                          value={search}
                          placeholder={props.t('search_unit')}
                        />
                      </div>
                      <div className="col">
                        <select
                          name="activity"
                          id="subActivity_activity"
                          value={filterByActivity}
                          className="form-control mb-2"
                          onChange={(event) => {
                            setFilterByActivity(
                              event.target.value === 'Choose an activity'
                                ? ''
                                : event.target.value === 'Choisissez une activité'
                                ? ''
                                : event.target.value
                            );
                            changePage({ selected: 0 });
                          }}
                        >
                          <option>{props.t('select_activity')}</option>
                          {activities.map((activity) => (
                            <option key={activity.id} value={activity.name}>
                              {activity.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {data.length === 0 ? (
                      <Alert variant="outlined" severity="info">
                        {props.t('no_np')}
                      </Alert>
                    ) : (
                      <NatureProductColumns data={data} />
                    )}
                    <br />
                    <div className="float-right">
                      {natureProducts.length > NUMBER_OF_NATUREPRODUCT_PER_PAGE && (
                        <ReactPaginate
                          previousLabel="<"
                          nextLabel=">"
                          pageCount={pageCount}
                          onPageChange={changePage}
                          containerClassName={styles['paginationButton']}
                          previousLinkClassName={styles['previousBtn']}
                          nextLinkClassName={styles['nextBtn']}
                          disabledClassName={styles['paginationDisabled']}
                          activeClassName={styles['paginationActive']}
                        />
                      )}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </Fragment>
      </div>

      <Modal isOpen={modelDetail} toggle={modelDetailToggle} centered>
        <ModalHeader>{props.t('detail_np')}</ModalHeader>
        <ModalBody>
          <DetailNatureProduct natureProduct={natureProduct} />
          <div>
            <Button color="secondary" className="float-right" onClick={modelDetailToggle}>
              {props.t('Cancel')}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};
export default translate(ListNatureProduct);
